import React, { useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  ChevronDown,
  X,
  Filter,
  Check,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  setMonth,
  setYear,
  addYears,
  subYears,
  isSameMonth,
  isSameYear,
  isWithinInterval,
  isBefore,
  isAfter,
  isValid,
  parse,
} from "date-fns";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export function DashboardToolbar({
  dateRange,
  setDateRange,
  onClear,
  className,
}) {
  const [mode, setMode] = useState("calendar");
  const [viewDate, setViewDate] = useState(new Date());
  const [inputs, setInputs] = useState({ from: "", to: "" });

  useEffect(() => {
    if (dateRange?.from) {
      setInputs({
        from: format(dateRange.from, "yyyy-MM-dd"),
        to: dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "",
      });
    } else {
      setInputs({ from: "", to: "" });
    }
  }, [dateRange]);

  const handleInputChange = (e, field) => {
    const val = e.target.value;
    setInputs((prev) => ({ ...prev, [field]: val }));

    const parsedDate = parse(val, "yyyy-MM-dd", new Date());
    if (isValid(parsedDate)) {
      if (field === "from") {
        setDateRange({ ...dateRange, from: parsedDate });
      } else {
        setDateRange({ ...dateRange, to: parsedDate });
      }
    }
  };

  const handlePresetChange = (value) => {
    const today = new Date();
    switch (value) {
      case "today":
        setDateRange({ from: today, to: today });
        break;
      case "last7":
        setDateRange({ from: subDays(today, 7), to: today });
        break;
      case "last30":
        setDateRange({ from: subDays(today, 30), to: today });
        break;
      case "thisMonth":
        setDateRange({ from: startOfMonth(today), to: endOfMonth(today) });
        break;
      case "lastMonth":
        const lastMonth = subMonths(today, 1);
        setDateRange({
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        });
        break;
      case "thisYear":
        setDateRange({ from: startOfYear(today), to: endOfYear(today) });
        break;
      case "lastYear":
        const lastYear = subYears(today, 1);
        setDateRange({ from: startOfYear(lastYear), to: endOfYear(lastYear) });
        break;
      default:
        break;
    }
  };

  const handleMonthSelect = (monthIndex) => {
    const targetDate = setMonth(viewDate, monthIndex);
    const startOfTarget = startOfMonth(targetDate);
    const endOfTarget = endOfMonth(targetDate);

    if (
      !dateRange?.from ||
      (dateRange.from &&
        dateRange.to &&
        !isSameMonth(dateRange.from, dateRange.to))
    ) {
      setDateRange({ from: startOfTarget, to: endOfTarget });
    } else {
      if (isBefore(startOfTarget, dateRange.from)) {
        setDateRange({ from: startOfTarget, to: endOfMonth(dateRange.from) });
      } else {
        setDateRange({ from: dateRange.from, to: endOfTarget });
      }
    }
  };

  const handleYearSelect = (year) => {
    const targetDate = setYear(viewDate, year);
    const startOfTarget = startOfYear(targetDate);
    const endOfTarget = endOfYear(targetDate);

    if (
      !dateRange?.from ||
      (dateRange.from &&
        dateRange.to &&
        !isSameYear(dateRange.from, dateRange.to))
    ) {
      setDateRange({ from: startOfTarget, to: endOfTarget });
    } else {
      if (isBefore(startOfTarget, dateRange.from)) {
        setDateRange({ from: startOfTarget, to: endOfYear(dateRange.from) });
      } else {
        setDateRange({ from: dateRange.from, to: endOfTarget });
      }
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: 12 },
    (_, i) => Math.floor(viewDate.getFullYear() / 12) * 12 + i
  );

  const isMonthInRange = (monthIndex) => {
    if (!dateRange?.from || !dateRange?.to) return false;
    const target = setMonth(viewDate, monthIndex);
    return isWithinInterval(target, {
      start: startOfMonth(dateRange.from),
      end: endOfMonth(dateRange.to),
    });
  };

  const isYearInRange = (year) => {
    if (!dateRange?.from || !dateRange?.to) return false;
    const target = setYear(viewDate, year);
    return isWithinInterval(target, {
      start: startOfYear(dateRange.from),
      end: endOfYear(dateRange.to),
    });
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-green-700 rounded-md">
        <Filter size={14} strokeWidth={2.5} />
        {/* <span className="text-xs font-bold uppercase tracking-wider">
          Filter
        </span> */}
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "h-9 justify-start text-left font-normal border-slate-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200 transition-all bg-white shadow-sm group",
              !dateRange && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-slate-500 group-hover:text-green-600 transition-colors" />
            <span className="text-sm text-slate-700 group-hover:text-green-800 truncate max-w-[200px]">
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "MMM dd, yyyy")} -{" "}
                    {format(dateRange.to, "MMM dd, yyyy")}
                  </>
                ) : (
                  format(dateRange.from, "MMM dd, yyyy")
                )
              ) : (
                "Select Period"
              )}
            </span>
            <ChevronDown className="ml-2 h-3 w-3 opacity-50 group-hover:text-green-600" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0 border-green-100 shadow-xl"
          align="end"
          sideOffset={8}
        >
          <div className="flex flex-col sm:flex-row h-[380px]">
            <div className="border-b sm:border-b-0 sm:border-r border-slate-100 bg-slate-50/50 p-2 flex flex-col gap-1 min-w-[140px]">
              <span className="text-[10px] font-bold text-slate-400 uppercase px-2 py-1">
                Quick Select
              </span>
              {[
                { label: "Today", value: "today" },
                { label: "Last 7 Days", value: "last7" },
                { label: "This Month", value: "thisMonth" },
                { label: "Last Month", value: "lastMonth" },
                { label: "This Year", value: "thisYear" },
                { label: "Last Year", value: "lastYear" },
              ].map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetChange(preset.value)}
                  className="text-xs text-left px-3 py-2 rounded-md hover:bg-white hover:shadow-sm hover:text-green-700 text-slate-600 font-medium transition-all flex items-center justify-between"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="p-3 flex flex-col w-[320px]">
              <div className="flex items-center justify-between mb-3 bg-slate-100 p-1 rounded-lg">
                {[
                  { id: "calendar", icon: CalendarRange, label: "Days" },
                  { id: "month", icon: CalendarDays, label: "Month" },
                  { id: "year", icon: CalendarIcon, label: "Year" },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    className={cn(
                      "flex items-center justify-center gap-1.5 flex-1 py-1.5 text-xs font-semibold rounded-md transition-all",
                      mode === m.id
                        ? "bg-white text-green-700 shadow-sm"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
                    )}
                  >
                    <m.icon size={13} />
                    {m.label}
                  </button>
                ))}
              </div>

              {mode === "calendar" && (
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <Input
                      type="date"
                      value={inputs.from}
                      onChange={(e) => handleInputChange(e, "from")}
                      className="h-8 text-xs border-slate-200 focus-visible:ring-green-500"
                    />
                    <ArrowRight className="h-3 w-3 text-slate-400" />
                    <Input
                      type="date"
                      value={inputs.to}
                      onChange={(e) => handleInputChange(e, "to")}
                      className="h-8 text-xs border-slate-200 focus-visible:ring-green-500"
                    />
                  </div>
                  
                </div>
              )}

              {mode === "month" && (
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4 px-2">
                    <button
                      onClick={() => setViewDate(subYears(viewDate, 1))}
                      className="p-1 hover:bg-slate-100 rounded-md text-slate-500 hover:text-green-600"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-bold text-slate-800">
                      {format(viewDate, "yyyy")}
                    </span>
                    <button
                      onClick={() => setViewDate(addYears(viewDate, 1))}
                      className="p-1 hover:bg-slate-100 rounded-md text-slate-500 hover:text-green-600"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 flex-1 content-start">
                    {Array.from({ length: 12 }).map((_, i) => {
                      const isSelected =
                        dateRange?.from &&
                        isSameMonth(setMonth(viewDate, i), dateRange.from);
                      const isRange = isMonthInRange(i);

                      return (
                        <button
                          key={i}
                          onClick={() => handleMonthSelect(i)}
                          className={cn(
                            "text-xs font-medium py-3 rounded-md border transition-all relative overflow-hidden",
                            isSelected
                              ? "bg-green-600 text-white border-green-600 shadow-md z-10"
                              : isRange
                              ? "bg-green-50 text-green-900 border-green-100"
                              : "border-slate-100 hover:border-green-200 hover:bg-green-50 hover:text-green-700 text-slate-600"
                          )}
                        >
                          {format(new Date(2000, i, 1), "MMMM")}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {mode === "year" && (
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4 px-2">
                    <button
                      onClick={() => setViewDate(subYears(viewDate, 12))}
                      className="p-1 hover:bg-slate-100 rounded-md text-slate-500 hover:text-green-600"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-bold text-slate-800">
                      {years[0]} - {years[years.length - 1]}
                    </span>
                    <button
                      onClick={() => setViewDate(addYears(viewDate, 12))}
                      className="p-1 hover:bg-slate-100 rounded-md text-slate-500 hover:text-green-600"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 flex-1 content-start">
                    {years.map((year) => {
                      const isSelected =
                        dateRange?.from &&
                        isSameYear(setYear(viewDate, year), dateRange.from);
                      const isRange = isYearInRange(year);
                      const isCurrent = new Date().getFullYear() === year;

                      return (
                        <button
                          key={year}
                          onClick={() => handleYearSelect(year)}
                          className={cn(
                            "text-sm font-semibold py-3 rounded-lg border transition-all",
                            isSelected
                              ? "bg-green-600 text-white border-green-600 shadow-md"
                              : isRange
                              ? "bg-green-50 text-green-900 border-green-100"
                              : isCurrent
                              ? "bg-slate-50 text-green-700 border-green-200"
                              : "border-slate-100 hover:border-green-200 hover:bg-green-50 hover:text-green-700 text-slate-600"
                          )}
                        >
                          {year}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {(dateRange?.from || dateRange?.to) && (
        <Button
          variant="ghost"
          onClick={() => {
            setDateRange({ from: undefined, to: undefined });
            if (onClear) onClear();
          }}
          className="h-9 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Clear Filters"
        >
          <X className="h-4 w-4" />
          <span className="ml-1 text-xs font-medium">Reset</span>
        </Button>
      )}
    </div>
  );
}
